import { useEffect } from "react";
import { useFormState, useWatch } from "react-hook-form";
import { get } from "lodash";
import { notification } from "@excent-one/react/notification";
import { Shimmer } from "@excent-one/react/indicators";
import { ControlledFileUploader } from "@excent-one/react/form/inputs";
import type { FileUploaderPermissionMode } from "@excent-one/react/file-uploader";
import { resolveIncidentAttachmentUrl } from "@phrmpc/shared/hooks/useIncidentAttachmentContent";
import type { IncidentRecordFileInfoDto } from "@phrmpc/infrastructure/phrmpc/models";
import { useIncidentRecordFiles } from "./IncidentRecordFileContext";
import {
  CASE_DOCUMENT_ALLOWED_EXTENSIONS,
  CASE_DOCUMENT_MAX_SIZE_BYTES,
} from "../utils/uploadLimits";

export interface IncidentUploadCellProps {
  control: any;
  setValue: any;
  name: string;
  incidentRecordId: string | undefined;
  fileFieldTypeId: number;
  isSignedDocument?: boolean;
  /** Picks the Nth match by upload order - see IncidentRecordFileContext's `getFile`. */
  matchIndex?: number;
  disabled?: boolean;
  required?: boolean;
  label: string;
  allowedExtensions?: string[];
  maxFileSize?: number;
  multiple?: boolean;
  widthPercent?: number;
  /** Additional RHF field names whose errors should also render here (first non-empty message
   *  wins) - see IncidentNarrativeSection's `incidentRecordFiles` collection-level use. */
  extraErrorNames?: string[];
  /** Renders a skeleton placeholder instead of the real control, matching how Controlled inputs
   *  cascade the page's `loading` state via `@excent-one/react`'s Shimmer. */
  loading?: boolean;
}

/** Shared by every IncidentUploadCell instance so they all agree on the same first-non-empty-
 *  message error resolution instead of each re-deriving it independently. */
function useUploaderErrorMessage(
  control: any,
  name: string,
  extraErrorNames?: string[],
) {
  const { errors } = useFormState({ control, name: [name, ...(extraErrorNames ?? [])] });
  return (
    (get(errors, name)?.message as string | undefined) ??
    extraErrorNames
      ?.map((extraName) => get(errors, extraName)?.message as string | undefined)
      .find(Boolean)
  );
}

/**
 * Real-upload replacement for the old LabeledUpload/mockUpload pattern. Mirrors
 * TransmittalReleaseAttachmentCell: upload/preview/remove wired to actual endpoints, disabled
 * until a real incidentRecordId exists. Resolves its file and upload/delete operations from
 * IncidentRecordFileContext instead of building its own FormData/customInstance calls - must be
 * rendered under an IncidentRecordFileProvider.
 *
 * Uses ControlledFileUploader (not the shared ControlledUpload) - ControlledUpload's file-list
 * seed effect only runs once at mount, which misses cases where the bound field's value arrives
 * asynchronously after mount instead of being present at mount. Resolving `file` from
 * IncidentRecordFileContext is exactly that case (a separate async record/files fetch, not
 * necessarily already resolved when this component first mounts) - the same class of bug fixed in
 * ControlledFileUploader for CaseDocumentTabStrip (see that component's reload-fix comment).
 */
const IncidentUploadCell = ({
  control,
  setValue,
  name,
  incidentRecordId,
  fileFieldTypeId,
  isSignedDocument = false,
  matchIndex = 0,
  disabled,
  required,
  label,
  // Bug 157916: defaults to the case-document rule (UploadIncidentRecordFileValidator.cs's
  // CaseDocumentMaxSizeBytes/CaseDocumentAllowedExtensions) since every caller of this component
  // except Incident Report Attachment/Evidence falls under it - those two override explicitly
  // with the larger LargeFileFieldTypeIds limits (see uploadLimits.ts).
  allowedExtensions = CASE_DOCUMENT_ALLOWED_EXTENSIONS,
  maxFileSize = CASE_DOCUMENT_MAX_SIZE_BYTES,
  multiple,
  extraErrorNames,
  loading,
}: IncidentUploadCellProps) => {
  const { getFile, getFiles, upload, remove } = useIncidentRecordFiles();
  const file = multiple
    ? undefined
    : getFile(fileFieldTypeId, isSignedDocument, matchIndex);
  const files = multiple ? getFiles(fileFieldTypeId, isSignedDocument) : [];

  const errorMessage = useUploaderErrorMessage(control, name, extraErrorNames);

  // The library ControlledFileUploader's `files` display comes straight from this field's RHF
  // value, and `onUpload`/`onDelete` below can only push back a placeholder (the upload endpoint
  // doesn't return the created file's id) or nothing at all - so the field must be independently
  // re-synced from IncidentRecordFileContext's `getFile`/`getFiles` (the real source of truth,
  // refetched after every upload/delete/confirm) whenever they resolve or change.
  //
  // `multiple` fields must write the full DTO array, not a single entry (Bug 157949): the record-
  // files query refetches after every upload, which re-fires this effect - writing a lone entry
  // here would clobber the field's already-correct array back down to one stale item on every
  // refetch.
  //
  // Bug 157950: watches the field's own *live* value (`useWatch`) and re-syncs whenever it
  // diverges from the resolved file(s), not just when the resolved file(s) themselves change.
  // IncidentForm's <Form values={initialValues}> is a controlled form - any time its own,
  // separate `["incident-record", id]` query settles (independently of this component's
  // `["incident-record-files", id]` query via IncidentRecordFileContext), the whole form resets,
  // wiping this field back to its DTO-derived default (null - it isn't a real IncidentRecordDto
  // property, so the reset can't restore it). If that reset lands after this effect already
  // hydrated the field once, `file?.id` alone never changes again, so a naive dependency-only
  // effect never re-fires and the field stays stuck on "No attachment" even though the file
  // genuinely exists and loads fine - the live-value comparison here makes the sync self-healing
  // regardless of which query wins the race.
  const watchedValue = useWatch({ control, name });
  const targetFileIdsKey = JSON.stringify(files.map((f) => f.id));
  useEffect(() => {
    if (multiple) {
      const currentIds = Array.isArray(watchedValue)
        ? watchedValue.map((f: IncidentRecordFileInfoDto) => f?.id)
        : [];
      const targetIds = files.map((f) => f.id);
      if (JSON.stringify(currentIds) !== JSON.stringify(targetIds)) {
        setValue(name, files, { shouldDirty: false });
      }
    } else if (file?.id && watchedValue?.id !== file.id) {
      setValue(name, file, { shouldDirty: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [multiple, file?.id, targetFileIdsKey, watchedValue, name, setValue, control]);

  const uploadFn = async (
    rawFile: File,
    onProgress?: (percent: number) => void,
  ): Promise<IncidentRecordFileInfoDto> => {
    if (!incidentRecordId) {
      notification.error("Save this record before attaching a file.");
      throw new Error("Missing incidentRecordId");
    }
    const result = await upload(rawFile, fileFieldTypeId, isSignedDocument, onProgress);
    // The upload endpoint doesn't return the created file's id - `getFile`/`getFiles` above
    // (sourced from IncidentRecordFileContext, which refetches after every upload) resolve the
    // authoritative DTO once that settles and are already bound as `files`/`onFilesChange` via
    // `control`/`name`, so this placeholder is only what briefly renders mid-upload.
    return { fileName: result.name };
  };

  const removeFn = async (target: IncidentRecordFileInfoDto) => {
    if (!target.id) return;
    await remove(target.id);
  };

  const canEdit = Boolean(!disabled && incidentRecordId);

  // Bug 157950 follow-up: this previously fell straight to "restricted" whenever not editable,
  // which hides the file content entirely (name-only, no preview/download) - correct for a
  // genuinely gated per-role/per-party view, but wrong for the ordinary "viewing a Display-page
  // record" case, which every field routed through this component hits. Nothing here (Incident
  // Report Attachment, Evidence, Resignation, Termination) has a business rule that content must
  // stay hidden from someone who can already see the record - "read-only" (previewable, no
  // upload/delete) is the correct non-editable state.
  const permissionMode: FileUploaderPermissionMode = canEdit
    ? "upload-delete"
    : "read-only";

  return (
    <Shimmer loading={loading} shimmerElements={["label", "input"]}>
      <ControlledFileUploader
        control={control}
        name={name}
        label={required ? `${label} *` : label}
        errorMessage={errorMessage}
        multiple={multiple}
        permissionMode={permissionMode}
        restrictions={{ allowedExtensions, maxFileSize }}
        getFileName={(f: IncidentRecordFileInfoDto) => f.fileName ?? ""}
        getFileUrl={(f: IncidentRecordFileInfoDto) =>
          incidentRecordId && f.id
            ? resolveIncidentAttachmentUrl(incidentRecordId, f.id, f.fileName ?? "")
            : undefined
        }
        onUpload={uploadFn}
        onDelete={removeFn}
      />
    </Shimmer>
  );
};

export default IncidentUploadCell;
